import { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { getTokenStore } from '@shared/api/token-store';
import { useMe } from '@/hooks/use-me';
import { useAuthStore } from '@shared/stores/auth-store';
import { useMyPermissions } from '@/hooks/use-admin-permissions';
import {
  requestAndRegisterPushToken,
  setupForegroundNotificationListener,
} from '@shared/services/firebase-push';
import { AdminSidebar } from './admin-sidebar';
import { AdminTopbar } from './admin-topbar';
import { AdminPendingApprovalView, AdminRejectedView, AdminNoPermissionView } from './admin-status-views';
import type { AdminSection } from '@shared/types/api';

const PATH_SECTION_MAP: Record<string, AdminSection> = {
  transactions: 'TRANSACTIONS',
  kyc: 'KYC',
  users: 'USERS',
  invoices: 'INVOICES',
  payouts: 'PAYOUTS',
  refunds: 'REFUNDS',
  authorities: 'AUTHORITIES',
  services: 'SERVICES',
  config: 'CONFIG',
  reports: 'REPORTS',
  feedbacks: 'FEEDBACKS',
  'audit-logs': 'AUDIT_LOGS',
};

const SECTION_PATH_MAP: Record<AdminSection, string> = {
  TRANSACTIONS: '/transactions',
  KYC: '/kyc',
  USERS: '/users',
  INVOICES: '/invoices',
  PAYOUTS: '/payouts',
  REFUNDS: '/refunds',
  AUTHORITIES: '/authorities',
  SERVICES: '/services',
  CONFIG: '/config',
  REPORTS: '/reports',
  FEEDBACKS: '/feedbacks',
  AUDIT_LOGS: '/audit-logs',
};

const SUPER_ADMIN_ONLY_PATHS = ['admin-verification', 'role-permissions'];

export function AdminLayout() {
  const location = useLocation();
  const hasToken = !!getTokenStore().getAccessToken();
  const { data: me, isLoading, isError } = useMe();
  const { data: myPermissions = [], isLoading: isPermsLoading } = useMyPermissions();
  const hydrated = useAuthStore((s) => s.hydrated);
  const session = useAuthStore((s) => s.session);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    try {
      return localStorage.getItem('admin_sidebar_collapsed') === 'true';
    } catch {
      return false;
    }
  });

  const toggleCollapsed = () => {
    setIsCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem('admin_sidebar_collapsed', String(next));
      } catch {}
      return next;
    });
  };

  useEffect(() => {
    if (hasToken) {
      void requestAndRegisterPushToken();
      const cleanup = setupForegroundNotificationListener();
      return () => cleanup();
    }
  }, [hasToken]);

  if (!hydrated || isLoading || isPermsLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 text-sm text-gray-500">
        Loading…
      </div>
    );
  }
  if (!hasToken) {
    return <Navigate to={`/login?next=${encodeURIComponent(location.pathname)}`} replace />;
  }
  if (isError) {
    getTokenStore().clear();
    return <Navigate to="/login" replace />;
  }

  // Defence in depth — if a non-admin token lands here, reject
  if (me && me.role !== 'ADMIN') {
    getTokenStore().clear();
    return <Navigate to="/login" replace />;
  }

  if (me && me.status === 'PENDING_ADMIN_APPROVAL') {
    return <AdminPendingApprovalView user={me} />;
  }

  if (me && me.status === 'REJECTED') {
    return <AdminRejectedView user={me} />;
  }

  const currentAdminRole = me?.adminRole || session?.adminRole;
  const isSuperAdmin = currentAdminRole === 'SUPER_ADMIN';
  const hasAnyPermission = myPermissions.some((p) => p.canView);

  // Determine first permitted section for redirection
  const firstPermitted = myPermissions.find((p) => p.canView);
  const firstPermittedPath = firstPermitted ? SECTION_PATH_MAP[firstPermitted.section] : null;

  // Path authorization check
  const pathSegments = location.pathname.split('/').filter(Boolean);
  const rootSegment = pathSegments[0] || '';

  let isAuthorizedForCurrentPath = true;
  if (!isSuperAdmin) {
    if (SUPER_ADMIN_ONLY_PATHS.includes(rootSegment)) {
      isAuthorizedForCurrentPath = false;
    } else if (rootSegment && PATH_SECTION_MAP[rootSegment]) {
      const requiredSection = PATH_SECTION_MAP[rootSegment];
      isAuthorizedForCurrentPath = myPermissions.some(
        (p) => p.section === requiredSection && p.canView,
      );
    }
  }

  return (
    <div className="flex h-screen h-[100dvh] min-h-0 w-full overflow-hidden bg-gray-50">
      <AdminSidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        showNav={isSuperAdmin || hasAnyPermission}
        isCollapsed={isCollapsed}
        onToggleCollapse={toggleCollapsed}
      />
      <div className="flex min-w-0 flex-1 flex-col h-full min-h-0 overflow-hidden">
        <AdminTopbar onMenuClick={() => setSidebarOpen(true)} />
        <main className="min-w-0 flex-1 overflow-y-auto flex flex-col min-h-0">
          {isSuperAdmin ? (
            <Outlet />
          ) : !hasAnyPermission ? (
            <AdminNoPermissionView adminRole={me?.adminRole ?? null} />
          ) : !isAuthorizedForCurrentPath ? (
            firstPermittedPath ? (
              <Navigate to={firstPermittedPath} replace />
            ) : (
              <AdminNoPermissionView adminRole={me?.adminRole ?? null} />
            )
          ) : (
            <Outlet />
          )}
        </main>
      </div>
    </div>
  );
}
